import { useQuery } from '@apollo/client';
import {
  GET_MSP_ROLE_FEATURES,
  GET_ROLE_PERMISSON
} from '@nile/banyan/src/Graphql/Mutations/role';
import {
  DetailsPageLayout,
  Generator,
  AppContext,
  toast,
  ERROR_CODES,
  Loader,
  DetailPageLoading,
  ALL_MODULES,
  PageLoading,
  SkeletonLoading,
  ImagePreviewModal,
  useProduct
} from '@nile/components';
import {
  TicketClient,
  AccountClient,
  GET_ALL_WORK_STATUSES,
  GET_ALL_TEMPLATELIST
} from '@nile/graphql';
import { ProjectDetailHeader } from '@nile/projects/src/Components/Headers';
import gql from 'graphql-tag';
import { cloneDeep, isEqual, sortBy } from 'lodash';
import React, { useState, useContext } from 'react';
import { Redirect } from 'react-router-dom';

import { GET_PROJECT } from '@nile/projects/src/Graphql';
import { ProjectContextProvider } from '@nile/projects/src/Contexts/ProjectContext';
import TabContext from '@nile/mangifera/src/Contexts/TicketContext';
import {
  ProjectCommunicationContainer,
  ProjectInsightContainer,
  KanbanViewContainer,
  ProjectKanbanContainer
} from '@nile/projects/src/Containers';
import { FormattedMessage } from 'react-intl';
import { PROJECT_SCHEMA } from '../Constants';
import styled from 'styled-components';
import dompurify from 'dompurify';
import {
  dompurifier,
  useCheckIsItProduct
} from '@nile/components/src/Utils/helpers';
import { useGlobalState } from '@nile/components/src/Managers/StateManager';

const ProjectDetailContainerWrapper = styled.div`
  .prj-w-60 {
    width: 40%;
    max-width: 40%;
  }
  .prj-w-40 {
    width: 60%;
    max-width: 60%;
  }
`;

const ProjectDetailContainer = (props) => {
  const [project, setProject] = useState({});
  const [statuses, setStatuses] = useState([]);
  const [view, setView] = useGlobalState('projectActiveView', 'detail');

  const tabContext = useContext(TabContext);
  const appContext = useContext(AppContext);
  const isMSPProduct = useProduct('MSP');

  const isItProduct = useCheckIsItProduct();

  const [features, setFeatures] = useState(
    appContext?.user?.role?.roleFeatureList || []
  );

  // TODO: Filter based on roles
  // const [role, setRole] = useState({});
  const { loading, error, data } = useQuery(GET_PROJECT, {
    variables: { projectId: props.id, isItProduct },
    fetchPolicy: 'network-only',
    skip: !props.id,
    onCompleted(responseData) {
      const title = dompurifier(responseData?.getProject?.title || '');
      const project = { ...responseData.getProject, title: title };
      setProject(project);
      // AccountClient.query({
      //   query: GET_MSP_ROLE_FEATURES
      // }).then((res) => console.log('list of feat', res));
      if (responseData.getProject?.loggedInUserRole) {
        AccountClient.query({
          query: GET_ROLE_PERMISSON,
          variables: {
            roleId: responseData.getProject?.loggedInUserRole?.roleId
          }
        }).then((res) => {
          if (res?.data?.getRolePermissions) {
            const updatedFeatures = cloneDeep(features);
            const projectRoles = res.data.getRolePermissions?.roleFeatureList.map(
              (role) => {
                const idx = updatedFeatures.findIndex(
                  (feat) => feat.feature.key === role.feature.key
                );
                if (idx >= 0) {
                  updatedFeatures[idx] = role;
                } else {
                  //temporary: adding feature to main list
                  updatedFeatures.push(role);
                }
              }
            );
            setFeatures(updatedFeatures);
          }
        });
      } else {
        setFeatures(appContext?.user?.role?.roleFeatureList || []);
      }
    }
  });

  const {
    loading: statusesLoading,
    error: statusesError,
    data: allStatuses
  } = useQuery(GET_ALL_WORK_STATUSES, {
    onCompleted(res) {
      setStatuses(res.getWorkStatuses);
    }
  });

  const {
    loading: templatesLoading,
    error: templatesError,
    data: allTemplates
  } = useQuery(GET_ALL_TEMPLATELIST);

  if (
    loading ||
    Object.keys(project).length === 0 ||
    statusesLoading ||
    templatesLoading
  ) {
    return (
      <SkeletonLoading
        hideStepNav={true}
        hideTable={true}
        showPlainCardView={true}
        showCardView={true}
        showBox={false}
      />
    );
  }

  if (!data) {
    return <Redirect to="/projects/allprojects" />;
  }

  const onlyUpdateTypes = ['deleteMilestone', 'addMilestone', 'addTask'];

  const handleOnlyStateUpdation = (type, val) => {
    const updatedProject = cloneDeep(project);
    switch (type) {
      case 'deleteMilestone':
        updatedProject.milestones = sortBy(
          updatedProject.milestones,
          'milestoneOrder'
        )
          .filter((milestone) => milestone.milestoneId !== val)
          .map((m, i) => ({ ...m, milestoneOrder: i + 1 }));
        setProject(updatedProject);
        break;
      case 'addMilestone':
        updatedProject.milestones.push(val);
        setProject(updatedProject);
        break;
      default:
        break;
    }
  };

  const difference = (object, base) => {
    const excludedProps = ['members'];
    let difference = {};
    for (var prop in object) {
      if (!isEqual(object[prop], base[prop]) && !excludedProps.includes(prop)) {
        difference[prop] = object[prop];
      }
    }
    return difference;
  };

  const getVariables = (diff) => {
    let variables = {};
    for (var prop in diff) {
      variables[prop] = {
        value: diff[prop],
        type: PROJECT_SCHEMA[prop],
        inputVariable: prop !== 'projectId',
        required: prop === 'projectId'
      };
    }
    return variables;
  };

  const getProjectFields = () => {
    const fieldsArr = [
      'projectId',
      'displayId',
      'title',
      'status',
      'scheduledStartDate',
      'dueDate',
      'overdue',
      'actualStartDate',
      'actualEndDate',
      {
        site: ['id', 'name']
      },
      {
        owner: ['userId', 'name', 'firstName', 'lastName', 'email']
      },
      {
        members: [
          { user: ['userId', 'name', 'firstName', 'lastName', 'email'] },
          'totalTasks',
          'completedTasks',
          'type'
        ]
      },
      {
        milestones: [
          'milestoneId',
          'name',
          'status',
          'totalTasks',
          'plannedTasks',
          'inProgressTasks',
          'blockedTasks',
          'completedTasks',
          'overdueTasks'
        ]
      },
      'totalTasks',
      'plannedTasks',
      'inProgressTasks',
      'blockedTasks',
      'completedTasks',
      'overdueTasks',
      'totalMilestones',
      'completedMilestones',
      {
        loggedInUserRole: ['roleId', 'name']
      }
    ];
    if (isMSPProduct) {
      fieldsArr.push({
        client: ['accountId', 'name']
      });
    }
    return fieldsArr;
  };

  const handleUpdate = (name, val) => {
    let value = val;
    if (name === 'title') {
      value = dompurifier(val);
    }
    if (onlyUpdateTypes.includes(name)) {
      handleOnlyStateUpdation(name, value);
      return;
    }
    const updatedProject = cloneDeep(project);
    updatedProject[name] = value;
    const diff = difference(updatedProject, project);
    if (Object.keys(diff).length === 0) {
      return;
    }
    diff.projectId = project.projectId;
    const generator = new Generator({
      operation: 'updateProject',
      variables: getVariables(diff),
      fields: getProjectFields()
    });
    const mutationString = generator.mutationBuilder();

    TicketClient.mutate({
      mutation: gql`
        ${mutationString.query}
      `,
      variables: diff,
      onCompleted(res) {
        console.log(res);
      },
      onError(res) {
        console.log(res);
      }
    })
      .then((res) => {
        if (res?.data?.updateProject) {
          // if (name === 'addMembers') {
          //   updatedProject.members = [...updatedProject.members, ...val];
          // }
          // if (name === 'deleteMembers') {
          //   const deletedMems = val.map((item) => item.userId);
          //   updatedProject.members = updatedProject.members.filter(
          //     (member) => !deletedMems.includes(member.userId)
          //   );
          // }
          tabContext.updateTab({
            module: ALL_MODULES.projectDetail,
            data: {
              projectId: res.data.updateProject.projectId,
              title: res.data.updateProject.title
            }
          });
          const newProjectKey = `${props.id}-${res.data.updateProject?.loggedInUserRole.roleId}`;
          if (newProjectKey !== props.projectKey) {
            props.setProjectKey(
              `${props.id}-${res.data.updateProject?.loggedInUserRole.roleId}`
            );
          }
          setProject(res.data.updateProject);
          toast('success', 'Properties updated successfully');
        }
      })
      .catch((err) => {
        // if (err?.graphQLErrors?.[0]?.extensions?.clientError?.[0]?.errorCode) {
        //   toast(
        //     'error',
        //     <FormattedMessage
        //       id={
        //         ERROR_CODES[
        //           err.graphQLErrors[0].extensions.clientError[0].errorCode
        //         ]
        //       }
        //     />
        //   );
        // } else {
        //   // toast('error', 'Something went wrong');
        // }
      });
  };

  const sections = [
    {
      id: 'conversation',
      component: (
        <ProjectCommunicationContainer
          project={project}
          setProject={setProject}
        />
      ),
      className: 'prj-w-60 full-height'
    },
    {
      id: 'details',
      component: (
        <ProjectInsightContainer
          project={project}
          handleUpdate={handleUpdate}
          setProject={setProject}
          taskTemplates={allTemplates}
        />
      ),
      className: 'prj-w-40 full-height'
    }
  ];

  const kanbanSection = [
    {
      id: 'milestones',
      component: (
        <ProjectKanbanContainer
          project={project}
          setProject={setProject}
          statuses={statuses}
        />
      ),
      className: 'full-width'
    }
  ];

  return (
    <ProjectDetailContainerWrapper className="full-height">
      <ProjectContextProvider value={{ features, project, handleUpdate }}>
        <DetailsPageLayout
          header={
            <ProjectDetailHeader
              project={project}
              handleUpdate={handleUpdate}
              setProject={setProject}
              setView={setView}
              view={view}
              taskTemplates={allTemplates}
            />
          }
          sections={view === 'detail' ? sections : kanbanSection}
          // singleSection={view === 'kanban' && kanbanSection}
        />
        <ImagePreviewModal />
      </ProjectContextProvider>
    </ProjectDetailContainerWrapper>
  );
};

export default ProjectDetailContainer;
